import { EnvironmentProviders, makeEnvironmentProviders } from '@angular/core';
import { Analytics, provideAnalytics } from '@angular/fire/analytics';
import { provideFirebaseApp } from '@angular/fire/app';
import { Auth, provideAuth } from '@angular/fire/auth';
import { Firestore, provideFirestore } from '@angular/fire/firestore';
import { Functions, provideFunctions } from '@angular/fire/functions';
import { providePerformance } from '@angular/fire/performance';
import { provideRemoteConfig } from '@angular/fire/remote-config';
import { provideStorage, Storage } from '@angular/fire/storage';
import { FirebaseOptions, initializeApp } from 'firebase/app';
import { getAnalytics, setConsent } from 'firebase/analytics';
import { connectAuthEmulator, getAuth } from 'firebase/auth';
import { connectFirestoreEmulator, getFirestore } from 'firebase/firestore';
import { connectFunctionsEmulator, getFunctions } from 'firebase/functions';
import { getPerformance } from 'firebase/performance';
import { getRemoteConfig, RemoteConfig, RemoteConfigSettings } from 'firebase/remote-config';
import { connectStorageEmulator, getStorage } from 'firebase/storage';
import { AngularFireAnalytics } from '@lib/firebase-compat-shims/analytics';
import { AngularFireAuth } from '@lib/firebase-compat-shims/auth';
import { AngularFirestore } from '@lib/firebase-compat-shims/firestore';
import { AngularFireFunctions } from '@lib/firebase-compat-shims/functions';
import { AngularFireStorage } from '@lib/firebase-compat-shims/storage';

export interface FirebaseProviderOptions {
  firebase: FirebaseOptions;
  functionsRegion: string;
  remoteConfigDefaults?: Record<string, string | number | boolean>;
  remoteConfigSettings?: RemoteConfigSettings;
  useEmulators?: boolean;
}

function getEmulatorHost(): string {
  return typeof location === 'undefined' ? 'localhost' : location.hostname;
}

function configureRemoteConfig(
  defaults?: Record<string, string | number | boolean>,
  settings?: RemoteConfigSettings,
): RemoteConfig {
  const remoteConfig = getRemoteConfig();
  remoteConfig.defaultConfig = defaults ?? {};
  if (settings) {
    remoteConfig.settings = settings;
  }
  return remoteConfig;
}

export function provideEkcFirebase(options: FirebaseProviderOptions): EnvironmentProviders {
  return makeEnvironmentProviders([
    provideFirebaseApp(() => initializeApp(options.firebase)),
    provideAuth(() => {
      const auth = getAuth();
      if (options.useEmulators) {
        connectAuthEmulator(auth, `http://${getEmulatorHost()}:9099`, { disableWarnings: true });
      }
      return auth;
    }),
    provideFirestore(() => {
      const firestore = getFirestore();
      if (options.useEmulators) {
        connectFirestoreEmulator(firestore, getEmulatorHost(), 8080);
      }
      return firestore;
    }),
    provideFunctions(() => {
      const functions = getFunctions(undefined, options.functionsRegion);
      if (options.useEmulators) {
        connectFunctionsEmulator(functions, getEmulatorHost(), 5001);
      }
      return functions;
    }),
    provideStorage(() => {
      const storage = getStorage();
      if (options.useEmulators) connectStorageEmulator(storage, getEmulatorHost(), 9199);
      return storage;
    }),
    provideAnalytics(() => {
      if (options.useEmulators) return {} as Analytics;
      const analytics = getAnalytics();
      setConsent({
        ad_personalization: 'denied',
        ad_storage: 'denied',
        analytics_storage: 'denied',
      });
      return analytics;
    }),
    providePerformance(() => (options.useEmulators ? ({} as ReturnType<typeof getPerformance>) : getPerformance())),
    provideRemoteConfig(() => configureRemoteConfig(options.remoteConfigDefaults, options.remoteConfigSettings)),
    {
      provide: AngularFireAuth,
      useFactory: (auth: Auth) => new AngularFireAuth(auth as unknown as import('firebase/auth').Auth),
      deps: [Auth],
    },
    {
      provide: AngularFirestore,
      useFactory: (firestore: Firestore) =>
        new AngularFirestore(firestore as unknown as import('firebase/firestore').Firestore),
      deps: [Firestore],
    },
    {
      provide: AngularFireFunctions,
      useFactory: (functions: Functions) =>
        new AngularFireFunctions(functions as unknown as import('firebase/functions').Functions),
      deps: [Functions],
    },
    {
      provide: AngularFireStorage,
      useFactory: (storage: Storage) =>
        new AngularFireStorage(storage as unknown as import('firebase/storage').FirebaseStorage),
      deps: [Storage],
    },
    {
      provide: AngularFireAnalytics,
      useFactory: (analytics: Analytics) =>
        new AngularFireAnalytics(analytics as unknown as import('firebase/analytics').Analytics, options.useEmulators),
      deps: [Analytics],
    },
  ]);
}
